import { useState } from "react";

import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "features/layout/components";
import { StartTrialButton } from "features/subscriptions/components";

import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomer from "common/datahooks/useCustomer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SubscriptionType } from "common/types";

import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";

import LockScreenNoPermissionsImage from "assets/images/lock-screen-no-permissions.png";

interface SubscriptionActivationProps {
  openManageSubscription: (subscriptionType: SubscriptionType) => void;
}

export default function SubscriptionActivation({
  openManageSubscription,
}: SubscriptionActivationProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionType>("agency");

  const {
    subscription_status,
    rental_subscriptions,
    agency_subscription_status,
    permissions,
  } = useSelectedWorkspaceContext();

  const {
    customerInfo: { had_trial },
  } = useCustomer();

  let mainComponent = (
    <div className="flex flex-col m-auto">
      <img
        className="mb-4 h-32 lg:h-40 mx-auto"
        src={LockScreenNoPermissionsImage}
        alt=""
      />
      <h2 className="text-xl-bold text-center mb-2">
        No seats found on this workspace
      </h2>
      <p className="text-sm text-black-500 text-center">
        Contact an administrator to resolve
      </p>
    </div>
  );

  if (permissions.includes("billing_full")) {
    const hasCancelledRental = rental_subscriptions?.some(
      ({ status }) => status === "cancelled",
    );

    const isExpired =
      subscription_status === "cancelled" ||
      hasCancelledRental ||
      agency_subscription_status === "cancelled";

    let actionButton = (
      <Button
        onClick={() => openManageSubscription(subscriptionType)}
        className="mt-auto ml-auto"
        size={isDesktop ? "md" : "lg"}
      >
        Continue
      </Button>
    );

    if (subscriptionType === "user" && !had_trial) {
      actionButton = (
        <StartTrialButton
          size={isDesktop ? "md" : "lg"}
          className="mt-auto ml-auto"
        />
      );
    }

    const Component = isDesktop ? DesktopLayout : MobileLayout;

    mainComponent = (
      <Component
        title={isExpired ? "Your subscription expired" : "Choose a plan"}
        subscriptionType={subscriptionType}
        setSubscriptionType={setSubscriptionType}
        actionButton={actionButton}
      />
    );
  }

  return (
    <ContentContainer>
      <ContentHeader>
        <PageTitle />
      </ContentHeader>
      {mainComponent}
    </ContentContainer>
  );
}
